// Y 軸ラベルの名前付き formatter(#1359)。
//
// サーバー(ERB / コントローラ)からは JS 関数を渡せないため、yaxis[].labels.formatter
// または options.yaxis_label_formatter に下の名前を書き、JS 側で関数に解決する。
// 名前は「値の単位」で選ぶ(同じ % 表示でも、値が 0〜100 か 0〜1 かで名前が違う)。

const numberFormatter = new Intl.NumberFormat("ja-JP")

// 数値に直せない値(null / 空文字 / 数字でない文字列)は null を返す
export function toFiniteNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  if (typeof value !== "string") return null

  const cleaned = value.trim().replace(/[,\s]/g, "").replace(/%$/, "")
  if (cleaned === "") return null
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

// 既定: K / M / B 単位の短縮表示(1,000 未満は桁区切り)
export function formatCompactNumber(value) {
  if (value === null || value === undefined || isNaN(value)) return value
  const abs = Math.abs(value)
  if (abs >= 1e9) return (value / 1e9).toFixed(1).replace(/\.0$/, "") + "B"
  if (abs >= 1e6) return (value / 1e6).toFixed(1).replace(/\.0$/, "") + "M"
  if (abs >= 1e4) return (value / 1e3).toFixed(1).replace(/\.0$/, "") + "K"
  if (abs >= 1e3) return (value / 1e3).toFixed(1).replace(/\.0$/, "") + "K"
  return numberFormatter.format(value)
}

// 秒数を時間・分・秒で出す(0 の単位は省く)。
// - 1時間以上: 秒は切り捨てて「5時間33分」まで(軸の目盛りに秒まで並ぶと読みにくいため)
// - 1分以上1時間未満: 「3分20秒」(秒は切り捨て)
// - 1分未満: 秒。小数の目盛り(0.5 秒刻みなど)が切り捨てで同じ表示にならないよう、小数1桁まで出す(例: 0.5秒)
export function formatDurationHms(totalSeconds) {
  if (totalSeconds < 0) return `-${formatDurationHms(-totalSeconds)}`
  // 1分未満は小数1桁に丸めて秒で出す。丸めて 60 秒以上になる値(59.96 など)は分の表示に回す
  const roundedSeconds = Number(totalSeconds.toFixed(1))
  if (roundedSeconds < 60) return `${roundedSeconds}秒`

  const seconds = Math.floor(Math.max(totalSeconds, roundedSeconds))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const parts = hours > 0
    ? [[hours, "時間"], [minutes, "分"]]
    : [[minutes, "分"], [seconds % 60, "秒"]]

  return parts.filter(([amount]) => amount !== 0).map(([amount, unit]) => `${amount}${unit}`).join("")
}

const withNumber = (format) => (value) => {
  const numericValue = toFiniteNumber(value)
  return numericValue === null ? value : format(numericValue)
}

// 名前 → formatter。値の単位:
// - number_delimited: 件数など。桁区切りで省略せずに出す(例: 12,345)
// - percentage: 0〜100 の % 値。小数1桁(例: 12.3%)
// - percentage_2: 0〜100 の % 値。小数2桁(例: 12.34%)
// - ratio_percentage: 0〜1 の比率を % にする。小数1桁(例: 0.123 → 12.3%)
// - seconds: 秒。小数1桁(例: 4.5秒)
// - duration_hms: 秒を時間・分・秒で出す(例: 5400 → 1時間30分。1時間以上は秒を切り捨て)
// - rank: 順位。整数に丸めて「位」を付ける(例: 6 → 6位)
export const Y_AXIS_LABEL_FORMATTERS = Object.freeze({
  number_delimited: withNumber((value) => numberFormatter.format(value)),
  percentage: withNumber((value) => `${value.toFixed(1)}%`),
  percentage_2: withNumber((value) => `${value.toFixed(2)}%`),
  ratio_percentage: withNumber((value) => `${(value * 100).toFixed(1)}%`),
  seconds: withNumber((value) => `${value.toFixed(1)}秒`),
  duration_hms: withNumber((value) => formatDurationHms(value)),
  rank: withNumber((value) => `${Math.round(value)}位`)
})

// 名前を formatter に解決する。名前が無ければ fallback、関数ならそのまま(JS で組み立てた config 用)、
// 未知の名前(JS 関数の文字列など)は console.warn して fallback
export function resolveYAxisLabelFormatter(name, fallback = formatCompactNumber) {
  if (name === undefined || name === null || name === "") return fallback
  if (typeof name === "function") return name
  if (isKnownYAxisLabelFormatter(name)) return Y_AXIS_LABEL_FORMATTERS[name]

  console.warn(
    `apex-chart: 未知の Y 軸ラベル formatter 名 ${JSON.stringify(name)} は使えないため、既定の書式で表示します。` +
    `使える名前: ${Object.keys(Y_AXIS_LABEL_FORMATTERS).join(", ")}`
  )
  return fallback
}

export function isKnownYAxisLabelFormatter(name) {
  return typeof name === "string" && Object.hasOwn(Y_AXIS_LABEL_FORMATTERS, name)
}

// ツールチップ(tooltip.y)とデータラベルの既定の値の書式。系列が載る Y 軸と同じ書式で出す(#1359)。
// 優先順位: 系列が載る軸の labels.formatter(名前) > チャート全体の yaxis_label_formatter(名前)
//           > 系列名による判定(「エンゲージメント率」は小数2桁の %) > 桁区切り
// - 系列が載る軸は ApexCharts が組み立てる w.globals.seriesYAxisReverseMap(系列番号 → 軸番号)から引く。
//   引けないときは、軸が1本なら軸0の書式を使い、2本以上ならどの軸か分からないので軸の書式を飛ばす
// - null / undefined / 数値に直せない値はそのまま返す(非表示の系列では値が undefined で渡る)
export function buildSeriesValueFormatter({ yaxis, chartFormatterName } = {}) {
  const axes = Array.isArray(yaxis) ? yaxis : [yaxis]
  const axisFormatterNames = axes.map((axis) => axis?.labels?.formatter)

  return (value, context = {}) => {
    const numericValue = toFiniteNumber(value)
    if (numericValue === null) return value

    const { seriesIndex, w } = context
    const mappedAxis = w?.globals?.seriesYAxisReverseMap?.[seriesIndex]
    let axisFormatterName
    if (Number.isInteger(mappedAxis)) {
      axisFormatterName = axisFormatterNames[mappedAxis]
    } else if (axisFormatterNames.length === 1) {
      axisFormatterName = axisFormatterNames[0]
    }
    if (isKnownYAxisLabelFormatter(axisFormatterName)) return Y_AXIS_LABEL_FORMATTERS[axisFormatterName](numericValue)
    if (isKnownYAxisLabelFormatter(chartFormatterName)) return Y_AXIS_LABEL_FORMATTERS[chartFormatterName](numericValue)

    const seriesName = w?.globals?.seriesNames?.[seriesIndex]
    if (typeof seriesName === "string" && seriesName.includes("エンゲージメント率")) return `${numericValue.toFixed(2)}%`

    return numberFormatter.format(numericValue)
  }
}
